import { getProgressionDrillById } from '../data/progression/drills';
import { getProgressionSkillById } from '../data/progression/skills';
import { getSkillTracksForSport } from '../data/progression/skillTracks';

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function getTrackName(sport, trackId) {
  return (
    getSkillTracksForSport(sport || '').find((track) => track.trackId === trackId)?.name ||
    'Core Progression'
  );
}

function formatStatusLabel(status) {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'draft':
      return 'Planned';
    default:
      return 'Planned';
  }
}

function formatDateLabel(timestamp) {
  const seconds = timestamp?.seconds;
  if (!seconds) return 'No date yet';
  return new Date(seconds * 1000).toLocaleDateString();
}

export function getStudentLessonAccountStatus(lesson = {}, currentUser = {}) {
  const userId = String(currentUser?.uid || '').trim();
  const userEmail = normalizeEmail(currentUser?.email || '');
  const lessonStudentId = String(lesson?.studentId || '').trim();
  const lessonEmail = normalizeEmail(
    lesson?.studentEmailNormalized || lesson?.studentEmail || ''
  );

  if (lessonStudentId && userId && lessonStudentId === userId) {
    return 'linked';
  }

  if (!lessonStudentId && lessonEmail && userEmail && lessonEmail === userEmail) {
    return 'pending_invite';
  }

  return 'unknown';
}

export function isLessonVisibleToStudent(lesson = {}, currentUser = {}) {
  const status = getStudentLessonAccountStatus(lesson, currentUser);
  return status === 'linked' || status === 'pending_invite';
}

export function mergeVisibleLessonsForStudent(lessons = [], currentUser = {}) {
  const merged = new Map();

  (lessons || []).forEach((lesson) => {
    const lessonId = lesson?.lessonId || lesson?.id;
    if (!lessonId) return;
    if (!isLessonVisibleToStudent(lesson, currentUser)) return;
    merged.set(lessonId, {
      ...lesson,
      lessonId,
      studentAccountStatus: getStudentLessonAccountStatus(lesson, currentUser),
    });
  });

  return Array.from(merged.values()).sort((a, b) => {
    const aTime = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
    const bTime = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
    return bTime - aTime;
  });
}

export function buildStudentLessonListItem(lesson = {}, { instructorName = '' } = {}) {
  const skill = getProgressionSkillById(lesson.targetSkillId);
  const sport = lesson.sport || '';

  return {
    lessonId: lesson.lessonId || '',
    sport,
    sportLabel: sport || 'No sport selected',
    status: lesson.status || 'draft',
    statusLabel: formatStatusLabel(lesson.status),
    dateLabel: formatDateLabel(lesson.updatedAt || lesson.createdAt),
    instructorName: instructorName || lesson.instructorId || 'Your instructor',
    studentName: lesson.studentName || '',
    targetSkillName: skill?.name || 'No skill selected yet',
    targetSkillDescription: skill?.description || '',
    trackName: getTrackName(sport, lesson.trackId || skill?.trackId || ''),
    hasLinkedVideo: Boolean(lesson.videoId),
    studentAccountStatus: lesson.studentAccountStatus || 'unknown',
  };
}

export function buildStudentLessonDetailViewModel(lesson = {}, { instructorName = '' } = {}) {
  const skill = getProgressionSkillById(lesson.targetSkillId);
  const trackName = getTrackName(lesson.sport, lesson.trackId || skill?.trackId || '');
  const stages = Array.isArray(lesson.stages)
    ? lesson.stages.map((stage) => ({
        key: stage?.stage || '',
        title: String(stage?.stage || '')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (character) => character.toUpperCase()),
        completionStatus: stage?.completionStatus || 'not_attempted',
        drillNames: Array.isArray(stage?.plannedDrillIds)
          ? stage.plannedDrillIds
              .map((drillId) => getProgressionDrillById(drillId)?.name || '')
              .filter(Boolean)
          : [],
        instructorNotes: stage?.instructorNotes || '',
      }))
    : [];

  const nextSkillNames = (skill?.nextSkillIds || [])
    .map((skillId) => getProgressionSkillById(skillId)?.name || '')
    .filter(Boolean);
  const recommendedNextSkillName =
    getProgressionSkillById(lesson.lessonOutcome?.recommendedNextSkillId)?.name || '';

  return {
    lessonId: lesson.lessonId || '',
    sport: lesson.sport || '',
    sportLabel: lesson.sport || 'No sport selected',
    targetSkillName: skill?.name || 'No skill selected yet',
    targetSkillDescription: skill?.description || 'No skill description has been added yet.',
    trackName,
    recommendationReason:
      lesson.recommendationReason || 'This lesson was planned directly by the instructor.',
    status: lesson.status || 'draft',
    statusLabel: formatStatusLabel(lesson.status),
    instructorName: instructorName || lesson.instructorId || 'Your instructor',
    dateLabel: formatDateLabel(lesson.updatedAt || lesson.createdAt),
    stages,
    lessonOutcome: {
      status: lesson.lessonOutcome?.status || 'in_progress',
      skillConfidence: lesson.lessonOutcome?.skillConfidence || 'developing',
      instructorSummary: lesson.lessonOutcome?.instructorSummary || '',
      recommendedNextSkillName,
    },
    nextSkillNames,
    hasLinkedVideo: Boolean(lesson.videoId),
    linkedVideoId: lesson.videoId || '',
    lessonFlowEmptyText:
      stages.length === 0
        ? 'A staged lesson flow has not been added to this lesson yet.'
        : '',
    studentAccountStatus: lesson.studentAccountStatus || 'unknown',
  };
}

